import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import {
  Shift,
  ShiftMode,
} from '../../../lib/api/settlementCompleteDateAdjustment';

/**
 * `buildShift()` 의 반환 타입.
 * 검증 실패 시 사용자에게 보여줄 한국어 메시지를 `error` 로,
 * 통과 시 Preview/Execute 요청에 그대로 쓸 `Shift` 페이로드를 `shift` 로 반환한다.
 *
 * `'error' in result` 로 좁히면 TS 가 `result.shift` 의 discriminated union 필수 필드를
 * narrow 해주므로 caller 측 non-null 단언이 불필요.
 */
export type BuildShiftResult = { shift: Shift } | { error: string };

type UseShiftConfigResult = {
  shiftMode: ShiftMode;
  setShiftMode: Dispatch<SetStateAction<ShiftMode>>;
  deltaDays: number;
  setDeltaDays: Dispatch<SetStateAction<number>>;
  targetCompleteDate: string | undefined;
  setTargetCompleteDate: Dispatch<SetStateAction<string | undefined>>;
  reason: string;
  setReason: Dispatch<SetStateAction<string>>;
  /**
   * 사전 검증 + Shift 페이로드 빌드를 한 번에 처리.
   * 통과: `{ shift }` / 실패: `{ error }`. caller 는 result-union 분기로 한번에 처리.
   */
  buildShift: () => BuildShiftResult;
  /** Execute 성공 시 reason 만 초기화 (mode/deltaDays 등은 다음 작업에서 재사용 가능성 큼). */
  resetReason: () => void;
};

/**
 * 변경 조건 폼(이동 방식 + 일수/타겟 날짜 + 사유) state 관리.
 *
 * mode 에 따라 필수 필드가 달라지는 discriminated union(`Shift`) 의 사전 검증과 빌드를
 * 한 함수에 묶어 caller(handlePreview/handleExecute) 가 단일 호출로 처리하도록 한다.
 * non-null 단언 없이 TS narrowing 만으로 안전성이 확보되는 구조.
 *
 * 완료일 변경 도메인은 default mode 를 `absolute` 로 둔다 — 운영자가 "특정 날짜로
 * 일괄 변경" 하는 시나리오가 더 빈번하기 때문 (delta 도 hidden 옵션으로 지원).
 */
export const useShiftConfig = (): UseShiftConfigResult => {
  const [shiftMode, setShiftMode] = useState<ShiftMode>('absolute');
  const [deltaDays, setDeltaDays] = useState<number>(0);
  const [targetCompleteDate, setTargetCompleteDate] = useState<string | undefined>();
  const [reason, setReason] = useState('');

  const buildShift = useCallback((): BuildShiftResult => {
    if (!reason.trim()) {
      return { error: '변경 사유를 입력해주세요.' };
    }
    if (shiftMode === 'delta') {
      if ((deltaDays ?? 0) === 0) {
        return { error: '이동 일수를 입력해주세요.' };
      }
      return { shift: { mode: 'delta', deltaDays } };
    }
    // shiftMode === 'absolute'
    if (!targetCompleteDate) {
      return { error: '목표 정산 완료일을 입력해주세요.' };
    }
    // 위 가드로 targetCompleteDate 가 string 으로 narrow 됨 — 단언 불필요.
    return { shift: { mode: 'absolute', targetCompleteDate } };
  }, [shiftMode, deltaDays, targetCompleteDate, reason]);

  const resetReason = useCallback((): void => {
    setReason('');
  }, []);

  return {
    shiftMode,
    setShiftMode,
    deltaDays,
    setDeltaDays,
    targetCompleteDate,
    setTargetCompleteDate,
    reason,
    setReason,
    buildShift,
    resetReason,
  };
};
